import { ComponentSearchViewState } from 'viewpointBuilder/selectContextTab/types';
import {
  APILoadTraversalParams,
  ComponentSelection,
  QueryBuilderQuery,
  Traversal,
} from '@ardoq/api-types';

export const getComponentSelection = (
  state: ComponentSearchViewState
): ComponentSelection =>
  state.startQuery
    ? { startSet: [], startQuery: state.startQuery }
    : { startSet: state.selectedComponentIds };

export const normalizeApiTraversalParams = (
  apiLoadTraversal: APILoadTraversalParams
): Traversal => {
  const {
    paths,
    filters,
    pathMatching,
    pathCollapsingRules,
    startSetExpansion,
  } = apiLoadTraversal;

  return {
    paths,
    filters,
    pathMatching,
    pathCollapsingRules,
    ...(startSetExpansion ? { startSetExpansion } : {}),
    componentSelection: apiLoadTraversal.startQuery
      ? { startSet: [], startQuery: apiLoadTraversal.startQuery }
      : { startSet: apiLoadTraversal.startSet ?? [] },
  };
};

/**
 * Heuristic replacement for the removed
 * deleted `StartContextSelectionType.ADVANCED_SEARCH_SELECT_ALL` signal. Used by the
 * viewpoint builder to decide which search tab to open when restoring state
 * from a URL.
 *
 * A `QueryBuilderQuery` always has the shape `[SearchContextRule, subquery]`.
 * Basic search produces queries whose subquery contains at most a `typeName`
 * filter and an optional `name` `contains_substring` filter, all flat. Anything
 * else — nested subqueries, additional fields, unusual operators — indicates
 * the query was built via Advanced Search.
 *
 * Known limitation: a user who opens Advanced Search and produces a query
 * shape indistinguishable from basic (e.g. just a typeName equality) will be
 * restored as Basic. Acceptable per the refactor's intent — the structural
 * data drives the experience.
 */
export const isAdvancedSearchQuery = (
  startQuery: QueryBuilderQuery
): boolean => {
  const subquery = startQuery.rules[1];
  if (!subquery || !('rules' in subquery)) return false;
  return subquery.rules.some(rule => !isBasicSearchRule(rule));
};

const BASIC_SEARCH_FIELDS = new Set(['typeName', 'name']);

const isBasicSearchRule = (
  rule: QueryBuilderQuery['rules'][1]['rules'][number]
): boolean => 'field' in rule && BASIC_SEARCH_FIELDS.has(rule.field);
